import {
    OrderTransferTargetData,
    OrderTransferTargetEntityModel,
    OrderTransferTargetReferenceData
} from "./order-transfer-target-entity.model";
import {computed, effect, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {EntityData, ValidationMessage} from "@fedelta-pos/data-client";
import {OrderingPhase, SessionModel} from "../session/session.model";
import {NavigationService} from "../navigation/navigation.service";
import {OrderType} from '../order/order';
import {OrderInitiationStatus, OrderModel, OrderSubmissionStatus} from '../order/order.model';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {GroupOrderPageMode} from "./group-order-page/group-order-page-mode";
import {IdentityData} from '../identity/identity-entity.model';
import {LoadingService} from "../ui-elements/loading/loading.service";
import {OrderTransferTargetSearchModel} from "./order-transfer-target-search.model";
import {OrderTransferTargetStatus} from "./order-transfer-request-poll.service";
import {uuid} from '../utils/string-utils';
import {onEach} from '../utils/observable-utils';
import {ConfigurationModel} from '../configuration/configuration.model';
import {MenuModel} from '../menu/menu.model';
import {IdentityModel} from '../identity/identity.model';

@Injectable({providedIn: "root"})
export class GroupOrderModel {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private menuModel: MenuModel = inject(MenuModel);
    private orderModel: OrderModel = inject(OrderModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    private orderTransferTargetEntityModel: OrderTransferTargetEntityModel = inject(OrderTransferTargetEntityModel);
    private orderTransferTargetSearchModel: OrderTransferTargetSearchModel = inject(OrderTransferTargetSearchModel);
    private loadingService: LoadingService = inject(LoadingService);
    private navigationService: NavigationService = inject(NavigationService);

    private static readonly ORDER_TRANSFER_POLL_INTERVAL: number = 5000;
    public static readonly BEACON_SCAN_TIMEOUT: number = 1000 * 60 * 60;

    private readonly _groupOrderStatus: WritableSignal<OrderTransferTargetStatus> = signal(OrderTransferTargetStatus.CLOSED);
    public readonly groupOrderStatus: Signal<OrderTransferTargetStatus> = this._groupOrderStatus.asReadonly();
    private readonly _currentGroupOrder: WritableSignal<OrderTransferTargetData> = signal(undefined);
    public readonly currentGroupOrder: Signal<OrderTransferTargetData> = this._currentGroupOrder.asReadonly();
    public readonly selectedTransferTarget: WritableSignal<OrderTransferTargetReferenceData> = signal(undefined);
    private readonly _preferredTransferTarget: WritableSignal<OrderTransferTargetReferenceData> = signal(undefined);
    public readonly preferredTransferTarget: Signal<OrderTransferTargetReferenceData> = this._preferredTransferTarget.asReadonly();
    public readonly lastTransferTargetProposed: WritableSignal<number> = signal(0);

    public readonly canHaveGroupOrder: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor() && !!this.sessionModel.beaconId() &&
        ((this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE && this.orderingContextModel.currentStore().allowTableGroupOrders && !!this.orderingContextModel.currentStore().table.tableNumber) ||
            (this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore().allowPickupGroupOrders)));

    public readonly orderTransferTarget: Signal<OrderTransferTargetData> = computed(() => this.orderTransferTargetEntityModel.dataSignal());
    public readonly canStartGroupOrder: Signal<boolean> = computed<boolean>(() => this.canHaveGroupOrder() && (!this.sessionModel.beaconScanTime() || (Date.now() - this.sessionModel.beaconScanTime() <= GroupOrderModel.BEACON_SCAN_TIMEOUT)));
    public readonly hasGroupOrder: Signal<boolean> = computed<boolean>(() => !!this.orderTransferTargetEntityModel?.dataSignal()?.id);
    public readonly showGroupOrderNotice: Signal<boolean> = computed<boolean>(() => this.hasGroupOrder() || !!this.orderModel.totalTransfers());

    private readonly canPollOrderTransfer: Signal<boolean> = computed<boolean>(() =>
        this.sessionModel.orderingPhase() === OrderingPhase.ORDERING &&
        !this.orderModel.hasOrderPlacement() &&
        this._groupOrderStatus() !== OrderTransferTargetStatus.SAVING);

    private openingGroupOrder: boolean = false;

    constructor() {
        console.log("--- GroupOrderModel");

        onEach(this.sessionModel.sessionReset$, () => {
            void this.closeGroupOrder(OrderTransferTargetStatus.CLOSED);
            this.selectedTransferTarget.set(undefined);
        });

        effect(async () => {
            if (this.sessionModel.orderingPhase() === OrderingPhase.PAYMENT && this.orderModel.initiatedOrder() && this.orderModel.initiatedOrder().availableTransferTargets.length) {
                this.lastTransferTargetProposed.set(this.orderModel.initiatedOrder().availableTransferTargets[0].created.getTime());
            }
        });

        effect(() => {
            if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATED) {
                this._preferredTransferTarget.set(this.orderModel.initiatedOrder()?.availableTransferTargets.length
                    ? (this.selectedTransferTarget()
                    ? this.orderModel.initiatedOrder().availableTransferTargets.find((target: OrderTransferTargetReferenceData) => target.id === this.selectedTransferTarget().id)
                    : undefined) ?? this.orderModel.initiatedOrder().availableTransferTargets[0]
                    : undefined);
            }
        });
    }

    public async promptForGroupOrderParticipation(): Promise<void> {
        if (this.configurationModel.isConsumerActor() &&
            !this.menuModel.itemOrderPending() &&
            !this.hasGroupOrder() &&
            !this.orderModel.totalTransfers() &&
            this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATED &&
            this.orderModel.initiatedOrder().availableTransferTargets.length &&
            this.orderModel.initiatedOrder().availableTransferTargets[0].created.getTime() > this.lastTransferTargetProposed()) {
            if (!this.identityModel.currentIdentity().firstName) {
                this.identityModel.currentIdentity().firstName = this.orderingContextModel.firstName();
                this.identityModel.currentIdentity.update(i => <IdentityData>EntityData.create(IdentityData).assign(i));
            }
            if (await this.navigationService.createPage("woa-group-order-page", {
                mode: GroupOrderPageMode.PARTICIPANTS,
                hideNewGroupOrder: true
            })) {
                this.orderingContextModel.firstName.set(this.identityModel.currentIdentity().firstName);
                this.orderingContextModel.firstNameTrusted.set(false);
            }
        }
    }

    public async openGroupOrder(): Promise<void> {
        if (this.openingGroupOrder) {
            return;
        }
        this.openingGroupOrder = true;
        try {
            if (!this.hasGroupOrder() && !this.orderModel.totalTransfers()) {
                await this.loadingService.performWithSpinner(async () => {
                    this.orderTransferTargetSearchModel.setFilterValue("excludeTargetId", this.orderTransferTargetEntityModel.dataSignal()?.id);
                    this.orderTransferTargetSearchModel.setFilterValue("orderType", OrderType.toString(this.orderingContextModel.orderType()));
                    this.orderTransferTargetSearchModel.setFilterValue("tableNumber", this.orderingContextModel.currentStore().table.tableNumber);
                    this.orderTransferTargetSearchModel.setFilterValue("beaconId", this.sessionModel.beaconId());
                    await this.orderTransferTargetSearchModel.retrieve();
                    this.orderModel.initiatedOrder().availableTransferTargets = this.orderTransferTargetSearchModel.dataSignal();
                });
                if (this.orderModel.initiatedOrder().availableTransferTargets.length) {
                    if (await this.navigationService.createPage("woa-group-order-page", {mode: GroupOrderPageMode.PARTICIPANTS})) {
                        this.orderingContextModel.firstName.set(this.identityModel.currentIdentity().firstName);
                        this.orderingContextModel.firstNameTrusted.set(false);
                    }
                    return;
                }
            }
            return this.navigationService.createPage("woa-group-order-page", {mode: GroupOrderPageMode.EDIT});
        } finally {
            this.openingGroupOrder = false;
        }
    }

    public async saveGroupOrder(creating: boolean = false, groupOrderName?: string, groupOrderId?: string): Promise<ValidationMessage[]> {
        this._groupOrderStatus.set(OrderTransferTargetStatus.SAVING);
        if (!this.hasGroupOrder()) {
            creating = true;
        }
        if (creating) {
            this.orderTransferTargetEntityModel.newEntity();
        }
        const target: OrderTransferTargetData = this.orderTransferTargetEntityModel.dataSignal();
        if (creating) {
            target.id = groupOrderId || uuid();
        }
        if (creating || groupOrderName) {
            target.name = groupOrderName;
        }
        target.appStore = this.orderingContextModel.currentStore().storeId;
        target.orderTypeEnum = this.orderingContextModel.orderType();
        target.tableNumber = this.orderingContextModel.currentStore().table.tableNumber;
        target.beaconId = this.sessionModel.beaconId();

        try {
            const validationMessages: ValidationMessage[] = await this.orderTransferTargetEntityModel.saveEntity();
            if (validationMessages) {
                if (creating) {
                    this.orderTransferTargetEntityModel.clearEntity();
                    this._currentGroupOrder.set(undefined);
                    this._groupOrderStatus.set(OrderTransferTargetStatus.CLOSED);
                }
                return validationMessages;
            }
            this._currentGroupOrder.set(this.orderTransferTargetEntityModel.dataSignal());
            this._groupOrderStatus.set(OrderTransferTargetStatus.OPEN);

            // Force order initiation to persist group order details locally in current order
            this.orderModel.requestOrderInitiation("save group order");

            setTimeout(() => this.pollOrderTransferRequests(), GroupOrderModel.ORDER_TRANSFER_POLL_INTERVAL);
            return undefined;
        } catch (error) {
            if (creating) {
                this.orderTransferTargetEntityModel.clearEntity();
                this._currentGroupOrder.set(undefined);
                this._groupOrderStatus.set(OrderTransferTargetStatus.CLOSED);
            }
            throw error;
        }
    }

    public async closeGroupOrder(closeStatus: OrderTransferTargetStatus, closeOnServer: boolean = true, removeItems: boolean = false): Promise<void> {
        if (this.hasGroupOrder() || !!this.orderModel.totalTransfers()) {
            if (this.hasGroupOrder()) {
                if (closeOnServer) {
                    await this.orderTransferTargetEntityModel.deleteEntity(this.orderTransferTargetEntityModel.dataSignal().id);
                }
                this.orderTransferTargetEntityModel.clearEntity();
                this._currentGroupOrder.set(undefined);
            } else {
                closeOnServer = false;
            }

            const itemsRemoved: boolean = removeItems && this.orderModel.removeTransferRequestOrderItems();

            // Request order initiation to persist removal of group order details locally in current order
            if (closeOnServer && !itemsRemoved) {
                this.orderModel.requestOrderInitiation("close group order");
            }
        }
        if (this._groupOrderStatus() !== closeStatus) {
            this._groupOrderStatus.set(closeStatus);
            if ((closeStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT || closeStatus === OrderTransferTargetStatus.CLOSED_CONFLICT)) {
                setTimeout(() => {
                    if (this.sessionModel.orderingPhase() === OrderingPhase.ORDERING &&
                        this.orderModel.orderSubmissionStatus() !== OrderSubmissionStatus.SUBMITTING &&
                        this.orderModel.orderSubmissionStatus() !== OrderSubmissionStatus.COMPLETE) {
                        this.openGroupOrder();
                    }
                });
            }
        }
    }

    public async restoreGroupOrder(): Promise<ValidationMessage[]> {
        return this.hasGroupOrder() ? this.saveGroupOrder() : undefined;
    }

    private async pollOrderTransferRequests() {
        if (this.hasGroupOrder()) {

            // Pause actual polling of the group order at the payment phase, but keep waiting until the group order is actually closed, as the payment could fail, or the user could go back from the checkout page.
            if (this.canPollOrderTransfer()) {
                const status: OrderTransferTargetStatus = await this.orderModel.pollTransferRequests(
                    this.orderTransferTargetEntityModel.dataSignal()?.id,
                    this.orderingContextModel.currentStore().table.tableNumber,
                    this.sessionModel.beaconId());
                if (this.canPollOrderTransfer() && status !== OrderTransferTargetStatus.OPEN && this._groupOrderStatus() === OrderTransferTargetStatus.OPEN) {
                    void this.closeGroupOrder(status, false);
                }
            }

            if (this.hasGroupOrder()) {
                setTimeout(() => this.pollOrderTransferRequests(), GroupOrderModel.ORDER_TRANSFER_POLL_INTERVAL);
            }
        }
    }

}
